import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Modular countdown overlay hook for TV displays.
 * Handles countdown to adhan/iqama times, jummah silence, forbidden prayer time, and overlay state management.
 *
 * @param {Object} options
 * @param {Object} options.todayPrayerTimes - Today's prayer times data
 * @param {Object} options.tomorrowPrayerTimes - Tomorrow's prayer times data
 * @param {boolean} options.testMode - Whether in test mode (disables automatic countdown)
 * @param {Object} options.tvSettings - TV settings from useTVSettings hook (optional)
 * @param {boolean} options.paused - Whether automatic countdown overlays should be deferred
 * @returns {Object} Overlay state and controls
 */
export function useCountdownOverlay({ todayPrayerTimes, tomorrowPrayerTimes, testMode = false, tvSettings = {}, paused = false }) {
  // Countdown state
  const [nextPrayer, setNextPrayer] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [activePrayer, setActivePrayer] = useState(null);
  const [countdownPhase, setCountdownPhase] = useState("adhan"); // "adhan" | "iqamah"
  const [countdownLabel, setCountdownLabel] = useState("");

  // Countdown overlay state
  const [overlayMode, setOverlayMode] = useState(null); // null | "countdown" | "adhan" | "iqama" | "jummah" | "forbidden"
  const [overlayPrayerName, setOverlayPrayerName] = useState("");
  const [overlayCountdownSeconds, setOverlayCountdownSeconds] = useState(0);
  const [overlayType, setOverlayType] = useState("adhan"); // "adhan" | "iqama" | "jummah" | "forbidden"
  const [screenDimmed, setScreenDimmed] = useState(false);
  const [iqamaExtraDimmed, setIqamaExtraDimmed] = useState(false);
  const [isTomorrowFajr, setIsTomorrowFajr] = useState(false);
  const [tomorrowFajrTimeStr, setTomorrowFajrTimeStr] = useState("");

  // Refs
  const overlayTimerRef = useRef(null);
  const announcementEndTimeRef = useRef(null);
  const countdownTargetTimeRef = useRef(null);
  const iqamaStartTimeRef = useRef(null);
  const sameAdhanIqamaRef = useRef(false);
  const pendingIqamaTimeRef = useRef(null);
  const forbiddenEndTimeRef = useRef(null);
  const jummahEndTimeRef = useRef(null);
  // Track when user manually dismissed an overlay - prevents re-trigger until next prayer
  const dismissedUntilRef = useRef(null);
  const pausedAtRef = useRef(null);

  // Parse time string to Date object
  const parseTime = useCallback((timeStr, baseDate = new Date()) => {
    if (!timeStr || typeof timeStr !== "string") return null;
    const cleaned = timeStr.trim().toUpperCase();
    const d = new Date(baseDate);

    const ampmMatch = cleaned.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/);
    const normalMatch = cleaned.match(/^(\d{1,2}):(\d{2})$/);

    let h, m;
    if (ampmMatch) {
      h = parseInt(ampmMatch[1], 10);
      m = parseInt(ampmMatch[2], 10);
      const ampm = ampmMatch[3];
      if (ampm === "PM" && h !== 12) h += 12;
      if (ampm === "AM" && h === 12) h = 0;
    } else if (normalMatch) {
      h = parseInt(normalMatch[1], 10);
      m = parseInt(normalMatch[2], 10);
    } else {
      return null;
    }

    d.setHours(h, m, 0, 0);
    return d;
  }, []);

  // Normalize prayer time by key (handle AM/PM inference)
  const normalizePrayerDateByKey = useCallback((date, timeStr, key) => {
    if (!date || !timeStr || timeStr.match(/AM|PM/i)) return date;
    const lowerKey = key.toLowerCase();
    if (lowerKey === "duhr" && date.getHours() < 11) {
      date.setHours(date.getHours() + 12);
    } else if (["asr", "maghrib", "isha"].includes(lowerKey) && date.getHours() < 12) {
      date.setHours(date.getHours() + 12);
    }
    return date;
  }, []);

  // Get prayer date times (adhan and iqama)
  const getPrayerDateTimes = useCallback((prayerKey, dayTimes, baseDate) => {
    if (!dayTimes) return { adhanDate: null, iqamahDate: null };
    const adhanStr = dayTimes.adhan;
    const iqamahStr = dayTimes.iqama;

    let adhanDate = adhanStr ? parseTime(adhanStr, baseDate) : null;
    let iqamahDate = iqamahStr ? parseTime(iqamahStr, baseDate) : null;

    adhanDate = normalizePrayerDateByKey(adhanDate, adhanStr, prayerKey);
    iqamahDate = normalizePrayerDateByKey(iqamahDate, iqamahStr, prayerKey);

    return { adhanDate, iqamahDate };
  }, [parseTime, normalizePrayerDateByKey]);

  // Get Jummah/Khutbah times for Fridays
  // Returns the effective jummah start time (khutbah if both exist, otherwise jummah)
  // and whether there's a separate khutbah time
  const getJummahTimes = useCallback((prayerTimes, baseDate) => {
    if (!prayerTimes) return { jummahDate: null, khutbahDate: null, hasKhutbah: false };
    
    // Get first jummah time (jummah.1 or jummah["1"])
    const jummahStr = prayerTimes.jummah?.["1"] || prayerTimes.jummah?.iqama;
    // Get first khutbah time
    const khutbahStr = prayerTimes.khutbah?.["1"] || prayerTimes.jummah?.khutbah;
    
    let jummahDate = jummahStr ? parseTime(jummahStr, baseDate) : null;
    let khutbahDate = khutbahStr ? parseTime(khutbahStr, baseDate) : null;
    
    // Normalize for PM (jummah is typically around noon-afternoon)
    if (jummahDate && jummahDate.getHours() < 11) {
      jummahDate.setHours(jummahDate.getHours() + 12);
    }
    if (khutbahDate && khutbahDate.getHours() < 11) {
      khutbahDate.setHours(khutbahDate.getHours() + 12);
    }
    
    // If both khutbah and jummah exist and are different, khutbah is the start time
    const hasKhutbah = khutbahDate && jummahDate && khutbahDate.getTime() !== jummahDate.getTime();
    
    return { jummahDate, khutbahDate, hasKhutbah };
  }, [parseTime]);

  // Clear overlay state (manual: when user dismisses, auto: when timer expires)
  const clearOverlay = useCallback((isManualDismiss = false) => {
    // If manually dismissed, prevent re-trigger for 5 minutes
    if (isManualDismiss) {
      dismissedUntilRef.current = new Date(Date.now() + 5 * 60 * 1000);
    }
    setOverlayMode(null);
    setOverlayPrayerName("");
    setScreenDimmed(false);
    setIqamaExtraDimmed(false);
    announcementEndTimeRef.current = null;
    iqamaStartTimeRef.current = null;
    countdownTargetTimeRef.current = null;
    sameAdhanIqamaRef.current = false;
    pendingIqamaTimeRef.current = null;
    forbiddenEndTimeRef.current = null;
    jummahEndTimeRef.current = null;
  }, []);

  // Main countdown calculation effect
  useEffect(() => {
    if (!todayPrayerTimes) return;
    if (testMode) return;
    if (paused) {
      if (!pausedAtRef.current) pausedAtRef.current = new Date();
      return;
    }

    const prayers = [
      { key: "fajr", name: "Fajr" },
      { key: "sunrise", name: "Sunrise", isSunrise: true },
      { key: "duhr", name: "Dhuhr" },
      { key: "asr", name: "Asr" },
      { key: "maghrib", name: "Maghrib" },
      { key: "isha", name: "Isha" },
    ];

    // Check if today is Friday for Jummah handling
    const checkDate = new Date();
    const isFriday = checkDate.getDay() === 5;
    
    // Get Jummah times if it's Friday
    const { jummahDate: jummahTime, khutbahDate: khutbahTime, hasKhutbah } = isFriday 
      ? getJummahTimes(todayPrayerTimes, checkDate) 
      : { jummahDate: null, khutbahDate: null, hasKhutbah: false };
    
    // Determine effective Jummah start time: khutbah if both exist, otherwise jummah
    const effectiveJummahStart = hasKhutbah ? khutbahTime : jummahTime;
    const hasJummah = isFriday && effectiveJummahStart;

    // Use configurable countdown threshold from settings (default 1 minute for adhan, 5 for iqama)
    const ADHAN_COUNTDOWN_THRESHOLD_MS = (tvSettings?.adhanCountdownMinutes ?? 1) * 60 * 1000;
    const IQAMA_COUNTDOWN_THRESHOLD_MS = (tvSettings?.iqamaCountdownMinutes ?? 5) * 60 * 1000;
    // Use configurable duration from settings, default to 2 minutes for adhan, 15 for iqama
    const ADHAN_ANNOUNCEMENT_DURATION_MS = (tvSettings?.adhanScreenDurationMinutes ?? 2) * 60 * 1000;
    const IQAMA_ANNOUNCEMENT_DURATION_MS = (tvSettings?.iqamaScreenDurationMinutes ?? 15) * 60 * 1000;
    // Check if adhan/iqama overlays are enabled
    const adhanEnabled = tvSettings?.adhanEnabled !== false; // default true
    const iqamaEnabled = tvSettings?.iqamaEnabled !== false; // default true

    const interval = setInterval(() => {
      const now = new Date();

      // Check if user manually dismissed - skip triggering new overlays until timeout
      const isDismissed = dismissedUntilRef.current && now < dismissedUntilRef.current;
      
      // Clear the dismissed state if it has expired
      if (dismissedUntilRef.current && now >= dismissedUntilRef.current) {
        dismissedUntilRef.current = null;
      }

      // If an Iqamah occurred while settings were open, show its screen once
      // settings close instead of losing the scheduled transition.
      if (pausedAtRef.current) {
        const pausedAt = pausedAtRef.current;
        pausedAtRef.current = null;

        if (iqamaEnabled && !isDismissed) {
          for (const p of prayers) {
            if (p.isSunrise || (p.key === "duhr" && hasJummah)) continue;

            const { iqamahDate } = getPrayerDateTimes(p.key, todayPrayerTimes[p.key], now);
            if (iqamahDate && iqamahDate >= pausedAt && iqamahDate <= now) {
              setOverlayMode("iqama");
              setOverlayPrayerName(p.name);
              setOverlayType("iqama");
              setScreenDimmed(true);
              setIqamaExtraDimmed(false);
              iqamaStartTimeRef.current = now;
              announcementEndTimeRef.current = new Date(now.getTime() + IQAMA_ANNOUNCEMENT_DURATION_MS);
              return;
            }
          }
        }
      }

      // Handle announcement mode transitions
      if (overlayMode === "adhan" || overlayMode === "iqama") {
        // Check if any iqama time has been reached while showing adhan - iqama overrides adhan
        if (overlayMode === "adhan" && iqamaEnabled) {
          for (const p of prayers) {
            const { iqamahDate } = getPrayerDateTimes(p.key, todayPrayerTimes[p.key], now);
            if (iqamahDate) {
              const timeDiff = Math.abs(now - iqamahDate);
              // If we're within 1 second of iqama time, switch to iqama
              if (timeDiff < 1000) {
                setOverlayMode("iqama");
                setOverlayPrayerName(p.name);
                setOverlayType("iqama");
                setScreenDimmed(true);
                setIqamaExtraDimmed(false);
                iqamaStartTimeRef.current = now;
                announcementEndTimeRef.current = new Date(now.getTime() + IQAMA_ANNOUNCEMENT_DURATION_MS);
                sameAdhanIqamaRef.current = false;
                pendingIqamaTimeRef.current = null;
                return;
              }
            }
          }
        }

        if (announcementEndTimeRef.current && now >= announcementEndTimeRef.current) {
          if (overlayMode === "adhan") {
            const shouldTransitionToIqama =
              iqamaEnabled && (
                sameAdhanIqamaRef.current ||
                (pendingIqamaTimeRef.current && now >= pendingIqamaTimeRef.current)
              );

            if (shouldTransitionToIqama) {
              setOverlayMode("iqama");
              setScreenDimmed(true);
              setIqamaExtraDimmed(false);
              iqamaStartTimeRef.current = now;
              announcementEndTimeRef.current = new Date(now.getTime() + IQAMA_ANNOUNCEMENT_DURATION_MS);
              sameAdhanIqamaRef.current = false;
              pendingIqamaTimeRef.current = null;
            } else {
              clearOverlay();
            }
          } else {
            clearOverlay();
          }
        }

        // Check for extra dimming after 1 minute of iqama
        if (overlayMode === "iqama" && iqamaStartTimeRef.current && !iqamaExtraDimmed) {
          const iqamaElapsed = now - iqamaStartTimeRef.current;
          if (iqamaElapsed >= 60 * 1000) {
            setIqamaExtraDimmed(true);
          }
        }

        return; // Don't update countdown during announcements
      }

      // Check if countdown finished
      if (overlayMode === "countdown" && countdownTargetTimeRef.current) {
        // If we're counting down to adhan, check if iqama time has arrived - iqama overrides (if enabled)
        if (overlayType === "adhan" && iqamaEnabled) {
          for (const p of prayers) {
            const { iqamahDate } = getPrayerDateTimes(p.key, todayPrayerTimes[p.key], now);
            if (iqamahDate) {
              const timeDiff = Math.abs(now - iqamahDate);
              // If we're within 1 second of iqama time, switch to iqama
              if (timeDiff < 1000) {
                countdownTargetTimeRef.current = null;
                setOverlayMode("iqama");
                setOverlayPrayerName(p.name);
                setOverlayType("iqama");
                setScreenDimmed(true);
                setIqamaExtraDimmed(false);
                iqamaStartTimeRef.current = now;
                announcementEndTimeRef.current = new Date(now.getTime() + IQAMA_ANNOUNCEMENT_DURATION_MS);
                sameAdhanIqamaRef.current = false;
                pendingIqamaTimeRef.current = null;
                return;
              }
            }
          }
        }

        if (now >= countdownTargetTimeRef.current) {
          countdownTargetTimeRef.current = null;
          if (overlayType === "adhan" && adhanEnabled) {
            setOverlayMode("adhan");
            announcementEndTimeRef.current = new Date(now.getTime() + ADHAN_ANNOUNCEMENT_DURATION_MS);
          } else if (overlayType !== "adhan" && iqamaEnabled) {
            setOverlayMode("iqama");
            setScreenDimmed(true);
            setIqamaExtraDimmed(false);
            iqamaStartTimeRef.current = now;
            announcementEndTimeRef.current = new Date(now.getTime() + IQAMA_ANNOUNCEMENT_DURATION_MS);
          } else {
            // Feature disabled, clear overlay
            clearOverlay();
          }
          return;
        }
      }

      // A zero-minute Iqamah countdown means skip the countdown and show the
      // Iqamah screen as soon as the scheduled time arrives. The interval can
      // tick slightly after the exact second, so allow one interval of leeway.
      if (
        overlayMode === null &&
        iqamaEnabled &&
        IQAMA_COUNTDOWN_THRESHOLD_MS === 0 &&
        !isDismissed
      ) {
        for (const p of prayers) {
          if (p.isSunrise || (p.key === "duhr" && hasJummah)) continue;

          const { iqamahDate } = getPrayerDateTimes(p.key, todayPrayerTimes[p.key], now);
          if (!iqamahDate) continue;

          const elapsedSinceIqamah = now - iqamahDate;
          if (elapsedSinceIqamah >= 0 && elapsedSinceIqamah < 2000) {
            setOverlayMode("iqama");
            setOverlayPrayerName(p.name);
            setOverlayType("iqama");
            setScreenDimmed(true);
            setIqamaExtraDimmed(false);
            iqamaStartTimeRef.current = now;
            announcementEndTimeRef.current = new Date(now.getTime() + IQAMA_ANNOUNCEMENT_DURATION_MS);
            sameAdhanIqamaRef.current = false;
            pendingIqamaTimeRef.current = null;
            return;
          }
        }
      }

      let foundNext = false;
      let targetPrayer = null;
      let targetTime = null;
      let phase = "adhan";
      let targetHasSameAdhanIqama = false;
      let targetIqamaTime = null;

      // Check today's prayers
      for (const p of prayers) {
        let adhanDate, iqamahDate;

        // Handle sunrise specially - it's stored as a string, not an object
        if (p.isSunrise) {
          const sunriseStr = todayPrayerTimes.sunrise;
          if (sunriseStr) {
            adhanDate = parseTime(sunriseStr, now);
            // Sunrise is always AM, no need to normalize
          }
          iqamahDate = null; // Sunrise has no iqama
        } else {
          const times = getPrayerDateTimes(p.key, todayPrayerTimes[p.key], now);
          adhanDate = times.adhanDate;
          iqamahDate = times.iqamahDate;
        }

        // On Fridays with Jummah, handle Dhuhr specially:
        // - Skip Dhuhr iqama entirely (let Jummah silence effect handle it)
        // - If Jummah time equals Dhuhr adhan, also skip the adhan screen
        if (p.key === "duhr" && hasJummah) {
          const jummahSameAsAdhan = adhanDate && effectiveJummahStart && 
            adhanDate.getTime() === effectiveJummahStart.getTime();
          
          // Always skip Dhuhr iqama on Fridays - Jummah silence effect handles it
          iqamahDate = null;
          
          if (jummahSameAsAdhan) {
            // Jummah is at same time as adhan - skip adhan too
            adhanDate = null;
          }
          // Otherwise, show Dhuhr adhan as normal, then Jummah silence effect takes over
        }

        if (!adhanDate && !iqamahDate) continue;

        const hasBoth = adhanDate && iqamahDate;
        const sameTime = hasBoth && adhanDate.getTime() === iqamahDate.getTime();

        if (adhanDate && now < adhanDate) {
          targetPrayer = p;
          targetTime = adhanDate;
          // For sunrise, use "sunrise" phase instead of "adhan" to show different label
          phase = p.isSunrise ? "sunrise" : "adhan";
          targetHasSameAdhanIqama = sameTime;
          targetIqamaTime = iqamahDate;
          foundNext = true;
          break;
        }

        if (hasBoth && !sameTime && adhanDate <= now && now < iqamahDate) {
          targetPrayer = p;
          targetTime = iqamahDate;
          phase = "iqamah";
          targetHasSameAdhanIqama = false;
          targetIqamaTime = null;
          foundNext = true;
          break;
        }

        if (!adhanDate && iqamahDate && now < iqamahDate) {
          targetPrayer = p;
          targetTime = iqamahDate;
          phase = "iqamah";
          targetHasSameAdhanIqama = false;
          targetIqamaTime = null;
          foundNext = true;
          break;
        }
      }

      // Check if post-Isha
      let isPostIshaIqama = false;
      if (!foundNext && todayPrayerTimes?.isha) {
        const { adhanDate: ishaAdhan, iqamahDate: ishaIqamah } = getPrayerDateTimes(
          "isha",
          todayPrayerTimes.isha,
          now
        );
        const ishaEndTime = ishaIqamah || ishaAdhan;
        if (ishaEndTime && now >= ishaEndTime) {
          isPostIshaIqama = true;
        }
      }

      // Check tomorrow's Fajr
      if (!foundNext && tomorrowPrayerTimes?.fajr) {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const { adhanDate } = getPrayerDateTimes(
          "fajr",
          tomorrowPrayerTimes.fajr,
          tomorrow
        );
        if (adhanDate && adhanDate > now) {
          targetPrayer = { key: "fajr", name: "Fajr" };
          targetTime = adhanDate;
          phase = "adhan";
          foundNext = true;

          if (isPostIshaIqama) {
            setIsTomorrowFajr(true);
            setTomorrowFajrTimeStr(tomorrowPrayerTimes.fajr.adhan || "");
          }
        }
      }

      // Reset tomorrow Fajr flag if not counting to tomorrow Fajr
      if (foundNext && !(targetPrayer?.key === "fajr" && isPostIshaIqama)) {
        setIsTomorrowFajr(false);
        setTomorrowFajrTimeStr("");
      }

      if (foundNext && targetTime) {
        setNextPrayer(targetPrayer);
        setActivePrayer(targetPrayer.key);
        setCountdownPhase(phase);

        if (isTomorrowFajr) {
          setCountdownLabel("Tomorrow Fajr");
        } else {
          // On Fridays with Jummah, show "JUMU'AH" instead of "DHUHR IQAMAH"
          const isJummahPhase = targetPrayer.key === "duhr" && hasJummah && phase === "iqamah";
          const prayerName = isJummahPhase ? "JUMU'AH" : targetPrayer.name.toUpperCase();
          
          if (phase === "sunrise") {
            setCountdownLabel(`${prayerName} in`);
          } else if (phase === "adhan") {
            setCountdownLabel(`${prayerName} ADHAN in`);
          } else if (isJummahPhase) {
            // For Jummah, just show "JUMU'AH in" without "IQAMAH"
            setCountdownLabel(`${prayerName} in`);
          } else {
            setCountdownLabel(`${prayerName} IQAMAH in`);
          }
        }

        const diff = targetTime - now;
        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown(`${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);

        const secondsRemaining = Math.ceil(diff / 1000);
        // Use appropriate threshold based on phase (adhan or iqama) - sunrise doesn't trigger overlay
        const currentThreshold = phase === "adhan" ? ADHAN_COUNTDOWN_THRESHOLD_MS : IQAMA_COUNTDOWN_THRESHOLD_MS;
        // Check if this overlay type is enabled - sunrise doesn't trigger overlay
        const isOverlayEnabled = phase === "adhan" ? adhanEnabled : (phase === "iqamah" ? iqamaEnabled : false);

        if (diff <= currentThreshold && diff > 0 && isOverlayEnabled) {
          if (overlayMode !== "countdown" && !isDismissed) {
            setOverlayMode("countdown");
            setOverlayPrayerName(targetPrayer.name);
            setOverlayType(phase);
            countdownTargetTimeRef.current = targetTime;

            if (phase === "adhan" && targetHasSameAdhanIqama) {
              sameAdhanIqamaRef.current = true;
              pendingIqamaTimeRef.current = null;
            } else if (phase === "adhan" && targetIqamaTime) {
              sameAdhanIqamaRef.current = false;
              pendingIqamaTimeRef.current = targetIqamaTime;
            } else {
              sameAdhanIqamaRef.current = false;
              pendingIqamaTimeRef.current = null;
            }
          }
          setOverlayCountdownSeconds(secondsRemaining);
        } else if (overlayMode === "countdown" && diff > currentThreshold && !countdownTargetTimeRef.current) {
          setOverlayMode(null);
        }
      } else {
        setNextPrayer(null);
        setCountdown("--:--:--");
        setActivePrayer(null);
        if (overlayMode === "countdown") {
          clearOverlay();
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [
    todayPrayerTimes,
    tomorrowPrayerTimes,
    overlayMode,
    overlayType,
    testMode,
    paused,
    iqamaExtraDimmed,
    isTomorrowFajr,
    getPrayerDateTimes,
    getJummahTimes,
    clearOverlay,
    tvSettings?.adhanCountdownMinutes,
    tvSettings?.iqamaCountdownMinutes,
    tvSettings?.adhanScreenDurationMinutes,
    tvSettings?.iqamaScreenDurationMinutes,
    tvSettings?.adhanEnabled,
    tvSettings?.iqamaEnabled,
  ]);

  // Effect for handling forbidden prayer time screen (after sunrise)
  useEffect(() => {
    if (!todayPrayerTimes || testMode) return;

    // If forbidden screen is disabled but overlay is currently showing, clear it
    if (!tvSettings?.forbiddenScreenEnabled) {
      if (overlayMode === "forbidden") {
        clearOverlay();
      }
      return;
    }

    // Don't interrupt existing adhan/iqama overlays
    if (overlayMode === "adhan" || overlayMode === "iqama" || overlayMode === "countdown") return;

    const interval = setInterval(() => {
      const now = new Date();

      // Check if user manually dismissed - skip triggering new overlays until timeout
      const isDismissed = dismissedUntilRef.current && now < dismissedUntilRef.current;
      if (isDismissed) return;

      // Get sunrise time
      const sunriseStr = todayPrayerTimes?.sunrise;
      if (!sunriseStr) return;

      const sunrise = parseTime(sunriseStr, now);
      if (!sunrise) return;

      const durationMinutes = tvSettings?.forbiddenScreenDurationMinutes || 20;
      const forbiddenEndTime = new Date(sunrise.getTime() + durationMinutes * 60 * 1000);

      // Check if we're in the forbidden period
      if (now >= sunrise && now < forbiddenEndTime) {
        // We're in the forbidden period
        if (overlayMode !== "forbidden") {
          setOverlayMode("forbidden");
          setOverlayType("forbidden");
          setScreenDimmed(false);
          forbiddenEndTimeRef.current = forbiddenEndTime;
        }

        // Update countdown
        const remainingMs = forbiddenEndTime - now;
        const remainingSeconds = Math.ceil(remainingMs / 1000);
        setOverlayCountdownSeconds(remainingSeconds);
      } else if (overlayMode === "forbidden") {
        // Forbidden period ended
        clearOverlay();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [
    todayPrayerTimes,
    testMode,
    tvSettings?.forbiddenScreenEnabled,
    tvSettings?.forbiddenScreenDurationMinutes,
    overlayMode,
    parseTime,
    clearOverlay,
  ]);

  // Effect for handling Jummah silence screen
  useEffect(() => {
    if (!todayPrayerTimes || testMode) return;

    // If jummah silence is disabled but overlay is currently showing, clear it
    if (!tvSettings?.jummahSilenceEnabled) {
      if (overlayMode === "jummah") {
        clearOverlay();
      }
      return;
    }

    // Don't interrupt existing adhan/iqama/forbidden overlays
    if (overlayMode === "adhan" || overlayMode === "iqama" || overlayMode === "countdown" || overlayMode === "forbidden") return;

    // Check if today is Friday
    const now = new Date();
    const isFriday = now.getDay() === 5;
    if (!isFriday) return;

    const interval = setInterval(() => {
      const now = new Date();

      // Check if user manually dismissed - skip triggering new overlays until timeout
      const isDismissed = dismissedUntilRef.current && now < dismissedUntilRef.current;
      if (isDismissed) return;

      // Get first jummah time (jummah["1"] is the primary format, jummah.iqama is legacy)
      const jummahStr = todayPrayerTimes?.jummah?.["1"] || todayPrayerTimes?.jummah?.iqama;
      // Get first khutbah time (khutbah["1"] is the primary format, jummah.khutbah is legacy)
      const khutbahStr = todayPrayerTimes?.khutbah?.["1"] || todayPrayerTimes?.jummah?.khutbah;
      
      // If both khutbah and jummah exist (and are different), use khutbah as start time
      const hasKhutbah = khutbahStr && jummahStr && khutbahStr !== jummahStr;
      
      // Determine start time - khutbah if available, otherwise jummah
      const jummahStartStr = hasKhutbah ? khutbahStr : jummahStr;
      if (!jummahStartStr) return;

      const jummahStart = parseTime(jummahStartStr, now);
      if (!jummahStart) return;

      const durationMinutes = tvSettings?.jummahSilenceDurationMinutes || 30;
      const jummahEndTime = new Date(jummahStart.getTime() + durationMinutes * 60 * 1000);

      // Check if we're in the Jummah silence period
      if (now >= jummahStart && now < jummahEndTime) {
        // We're in the Jummah silence period
        if (overlayMode !== "jummah") {
          setOverlayMode("jummah");
          setOverlayType("jummah");
          setScreenDimmed(true);
          setIqamaExtraDimmed(false);
          jummahEndTimeRef.current = jummahEndTime;
        }

        // Determine when to extra dim based on whether khutbah exists
        const elapsedMs = now - jummahStart;
        
        if (hasKhutbah) {
          // With khutbah: extra dim when jummah time (prayer time) is reached
          const jummahPrayerTime = parseTime(jummahStr, now);
          if (jummahPrayerTime && now >= jummahPrayerTime && !iqamaExtraDimmed) {
            setIqamaExtraDimmed(true);
          }
        } else {
          // Without khutbah: split duration in half (first half dimmed, second half extra dimmed)
          const halfDurationMs = (durationMinutes * 60 * 1000) / 2;
          if (elapsedMs >= halfDurationMs && !iqamaExtraDimmed) {
            setIqamaExtraDimmed(true);
          }
        }
      } else if (overlayMode === "jummah") {
        // Jummah silence period ended
        clearOverlay();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [
    todayPrayerTimes,
    testMode,
    tvSettings?.jummahSilenceEnabled,
    tvSettings?.jummahSilenceDurationMinutes,
    overlayMode,
    iqamaExtraDimmed,
    parseTime,
    clearOverlay,
  ]);

  // Format time for display
  const formatTimeDisplay = useCallback((timeStr, prayerKey = null) => {
    if (!timeStr) return "-";
    if (timeStr === "-") return "-";

    if (timeStr.match(/AM|PM/i)) return timeStr;

    const [hStr, mStr] = timeStr.split(":");
    if (hStr && mStr) {
      let h = parseInt(hStr);
      const m = parseInt(mStr);

      if (prayerKey) {
        const key = prayerKey.toLowerCase();
        if (["asr", "maghrib", "isha"].includes(key)) {
          if (h < 12) h += 12;
        } else if (key === "duhr") {
          if (h === 12) {
            // 12 PM
          } else if (h >= 1 && h < 11) {
            h += 12;
          }
        } else if (key === "jummah") {
          if (h < 11) h += 12;
        }
      }

      const d = new Date();
      d.setHours(h, m);
      return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    }
    return timeStr;
  }, []);

  return {
    // Countdown state
    nextPrayer,
    countdown,
    activePrayer,
    countdownPhase,
    countdownLabel,

    // Overlay state
    overlayMode,
    overlayPrayerName,
    overlayCountdownSeconds,
    overlayType,
    screenDimmed,
    iqamaExtraDimmed,
    isTomorrowFajr,
    tomorrowFajrTimeStr,

    // Controls
    clearOverlay,
    setOverlayMode,
    setOverlayPrayerName,
    setOverlayType,
    setOverlayCountdownSeconds,
    setScreenDimmed,
    setIqamaExtraDimmed,

    // Utilities
    formatTimeDisplay,
  };
}

export default useCountdownOverlay;
